/**
 * What the posting form sends, and what it refuses — `W4-T10` §2.2.
 *
 * Pure, so the rules are readable in one place and the route action is only plumbing: `FormData` in,
 * either the contract's `JobDraftInput` or a key per field out. Keys, never sentences — the component
 * translates, and an action that returned prose would be the one place deciding what language
 * somebody reads (`job.tsx` makes the same choice).
 *
 * **Stricter than the contract, on purpose.** `JobDraftInputSchema` accepts `{}` because a draft may
 * be half-finished. This form publishes, and a published job with no category matches no feed —
 * the feed matches on category (`W4-T07` §2.1) — so title, description and one trade are required
 * here even though the API would take a job without them. Since `W4-T11` so is **the address**, for
 * the same reason: the feed skips a job with none (`W4-T07` AC5).
 */
import { JobUrgencySchema, type JobDraftInput } from '@marketplace/contracts';
import { readAddress, type AddressField } from '../../shared/address.js';

export type JobFormField = 'title' | 'description' | 'categorySlugs' | 'budget' | AddressField;

export type JobFormResult =
  { ok: true; input: JobDraftInput } | { ok: false; errors: Partial<Record<JobFormField, string>> };

/**
 * Euros as a person types them, to integer cents. `120`, `120,5`, `120,50` and `120.50` all parse;
 * anything else — thousands separators included — is `undefined`, and the caller says so.
 *
 * Integer arithmetic on the two halves rather than `Math.round(parseFloat(x) * 100)`: `1.005 * 100`
 * is `100.49999999999999`, and a price is the last place to learn that.
 */
export function eurosToCents(typed: string): number | undefined {
  const match = /^(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(typed.trim());
  if (match === null) return undefined;
  const [, whole = '0', fraction = ''] = match;
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
}

function text(form: FormData, name: string): string {
  const value = form.get(name);
  return typeof value === 'string' ? value.trim() : '';
}

export function parseJobForm(form: FormData): JobFormResult {
  const errors: Partial<Record<JobFormField, string>> = {};

  const title = text(form, 'title');
  const description = text(form, 'description');
  const categorySlugs = form
    .getAll('categorySlugs')
    .filter((v): v is string => typeof v === 'string');
  const urgency = JobUrgencySchema.safeParse(text(form, 'urgency'));

  if (title === '') errors.title = 'jobForm.error.title';
  else if (title.length > 140) errors.title = 'jobForm.error.titleLong';
  if (description === '') errors.description = 'jobForm.error.description';
  else if (description.length > 5000) errors.description = 'jobForm.error.descriptionLong';
  if (categorySlugs.length === 0) errors.categorySlugs = 'jobForm.error.categories';

  // Both ends optional, and "I don't know" — neither typed — sends no budget at all.
  const minTyped = text(form, 'budgetMin');
  const maxTyped = text(form, 'budgetMax');
  const minCents = minTyped === '' ? null : eurosToCents(minTyped);
  const maxCents = maxTyped === '' ? null : eurosToCents(maxTyped);
  if (minCents === undefined || maxCents === undefined) {
    errors.budget = 'jobForm.error.budgetFormat';
  } else if (minCents !== null && maxCents !== null && minCents > maxCents) {
    errors.budget = 'jobForm.error.budgetOrder';
  }

  // `W4-T11` §3.5. The same four lines and rules as a provider's base address.
  const { address, errors: addressErrors } = readAddress(form, 'jobForm');
  Object.assign(errors, addressErrors);

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    input: {
      title,
      description,
      categorySlugs,
      ...(urgency.success ? { urgency: urgency.data } : {}),
      ...(minCents === null && maxCents === null
        ? {}
        : { budget: { minCents: minCents ?? null, maxCents: maxCents ?? null } }),
      address,
      // Absent unless ticked: remembering is something a person asks for (§3.4).
      ...(form.get('rememberAddress') === 'on' ? { rememberAddress: true } : {}),
    },
  };
}
